// Field definitions and helpers for Upcoming Trips (TaskPogoTrip). Unlike
// logbook types there is no registry here: trips are a single fixed shape and
// every editable field is column-backed on the model. The reserved `fields`
// JSON blob (future waypoints / coordinates / reminder state) is deliberately
// NOT an editable field -- the edit form never touches it.
//
// Shared by the browser and, in the future, the reminder-email Lambdas.

import { resolvePlaceName } from './PlaceFields.mjs';

export const tripFields = [
  // Trip Name leads (ahead of Date) so it's the first field on the trip detail
  // page -- the natural thing to name a trip by, and where initial focus lands.
  //
  // `type: 'trip'` is the single marker for a trip-binding column, the way
  // `type: 'place'` is for a gazetteer one. It renders a typeahead over the
  // item's existing trips, and `idKey` names the second key that input owns:
  // picking a trip writes its `tripGroupId`, typing a diverging name clears it.
  // The name no longer decides which trip a leg is in -- the id does -- which
  // is what lets two trips be called "Day" (see TripIdentity.mjs).
  { name: 'Trip Name', key: 'name', type: 'trip', idKey: 'tripGroupId' },
  { name: 'Date', key: 'date', type: 'date' },
  // Days is what dates the NEXT leg: a leg's date plus its Days is where the
  // one behind it starts (TripGroups.cascadeLegDates). It's listed next to Date
  // for that reason, and the legs table shows the two side by side -- but the
  // trip detail page renders it after the waypoint list, beside Distance (see
  // Trip.jsx), so this position is not what the edit form reads.
  { name: 'Days', key: 'days', type: 'number', step: 1, min: 1 },
  // `place` rather than `text`, matching the Captain's / Pilot's registry:
  // these are gazetteer names (TaskPogoPlace), and `type === 'place'` is the
  // single marker every read-only list keys off to show the row's short name
  // (resolvePlaceName). It changes no INPUT here -- the trip page edits these
  // two through the waypoint list's Start/End rows, so nothing feeds them to
  // an EditForm; `coerceType` puts `place` on the text branch anyway.
  { name: 'From', key: 'from', type: 'place' },
  { name: 'To', key: 'to', type: 'place' },
  { name: 'Distance', key: 'distance', type: 'number', step: 'any' },
  { name: 'Notes', key: 'notes', type: 'text' },
];

// Columns the trip *legs* table shows (subset of tripFields, by key). Notes is
// long-form and gets its own flag column in the table instead. Trip Name is
// omitted because these tables render within a single named trip where it's
// redundant.
export const tripListColumns = ['date', 'days', 'from', 'to', 'distance'];

// How many days the leg stays at its destination, as everything scheduling a
// trip must read it: a whole number, at least 1. A leg always occupies a day,
// so a blank/missing/unusable value means 1 rather than 0 -- two legs sharing a
// date would leave the trip with no order (position IS the date; see
// tripDateMs), which is the ambiguity the days chain exists to remove.
export function legDays(trip) {
  const n = Number(trip?.days);
  return Number.isFinite(n) ? Math.max(1, Math.round(n)) : 1;
}

// Form validation for a trip leg (useAutosave's `validate`). Only `days` can be
// wrong: the number input hands back whatever parseFloat made of the keystrokes,
// and the column is an AppSync Int, so a 0 or a 2.5 is a hard mutation error
// with no field context. Holding the save with an inline message instead.
//
// Empty is VALID -- it means 1 (legDays) -- because clearing the field to retype
// it would otherwise park the record in an error state mid-keystroke.
export function validateTripLeg(state) {
  const raw = state?.days;
  if (raw === null || raw === undefined || raw === '') return { ok: true };
  const n = Number(raw);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1) {
    return {
      ok: false,
      fieldErrors: { days: 'Days must be a whole number, 1 or more.' },
    };
  }
  return { ok: true };
}

// A short human label for a trip leg: its name when set, otherwise
// "<From> - <To>", skipping blanks. The date is not included because rows
// render it in their own column.
// `placeIndex` is optional and only reaches the From/To fallback: a trip the
// user NAMED keeps that name verbatim, whatever the gazetteer thinks. With no
// index this returns exactly what it always did, which is how the export file
// name stays long.
export function tripTitle(trip, { placeIndex } = {}) {
  const name = trip?.name;
  if (name != null && String(name).trim() !== '') {
    return String(name).trim();
  }
  const parts = [trip?.from, trip?.to]
    .filter((v) => v != null && String(v).trim() !== '')
    .map((v) => resolvePlaceName(String(v).trim(), placeIndex));
  return parts.length ? parts.join(' - ') : 'Trip';
}

// True when the trip is dated the start of `now`'s local day or later -- a
// trip earlier today still counts as upcoming. Missing/invalid dates are not
// upcoming.
export function isUpcomingTrip(trip, now = new Date()) {
  if (!trip || !trip.date) return false;
  const date = new Date(trip.date);
  if (isNaN(date.getTime())) return false;
  const startOfDay = new Date(now);
  startOfDay.setHours(0, 0, 0, 0);
  return date >= startOfDay;
}

// A trip's date as a sortable instant. This is THE ordering key for trip legs
// -- a leg has no order column, so its position within a trip is expressed
// purely through `date` -- and everything that puts legs in order has to agree
// on it, or a leg can sit at one index on screen and another in the code that
// reorders it. An unparseable or missing date sorts first rather than poisoning
// the comparator with NaN (which would leave the sort order unspecified).
//
// Lives here rather than in TripGroups.mjs because TripGroups already imports
// from this module; the reverse import would be a cycle.
export function tripDateMs(trip) {
  const t = new Date(trip?.date).getTime();
  return Number.isNaN(t) ? 0 : t;
}

// Soonest-first copy of `trips`; does not mutate the input.
export function sortTripsAscending(trips) {
  return [...(trips || [])].sort((a, b) => tripDateMs(a) - tripDateMs(b));
}
